import React, { useEffect, useState } from 'react';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  staffName: string;
  idCard: string;
  confirmText: string;
  isLoading?: boolean;
}

export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  staffName,
  idCard,
  confirmText,
  isLoading = false
}: ConfirmationDialogProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsAnimating(true);
      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden';
    } else {
      setIsAnimating(false);
      // Re-enable body scroll when dialog closes
      document.body.style.overflow = 'unset';
      // Hide after animation completes
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, isLoading, onClose]);

  if (!isVisible) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  return (
    <div 
      className={`dialog-overlay ${isAnimating ? 'dialog-overlay-enter' : 'dialog-overlay-exit'}`}
      onClick={handleOverlayClick}
    >
      <div 
        className={`dialog-card ${isAnimating ? 'dialog-card-enter' : 'dialog-card-exit'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h2 style={{
          fontFamily: 'Figtree',
          fontWeight: 700,
          fontSize: '20px',
          color: '#1f2937',
          textAlign: 'center',
          margin: '0 0 16px 0'
        }}>
          {title}
        </h2>

        {/* Message */}
        <p style={{
          fontFamily: 'Figtree',
          fontWeight: 400,
          fontSize: '16px',
          color: '#6b7280',
          textAlign: 'center',
          margin: '0 0 8px 0'
        }}>
          {message.replace('[Staff Name]', staffName)}
        </p>

        {/* Staff ID */}
        <p style={{
          fontFamily: 'Figtree',
          fontWeight: 400,
          fontSize: '14px',
          color: '#9ca3af',
          textAlign: 'center',
          margin: '0 0 32px 0'
        }}>
          ID Card: {idCard}
        </p>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`flex-1 h-12 bg-white border border-[#e5e7eb] rounded-[24px] px-6 transition-colors duration-200 ${
              isLoading 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:bg-[#f9fafb] cursor-pointer'
            }`}
            style={{
              fontFamily: 'Figtree',
              fontWeight: 500,
              fontSize: '16px',
              color: '#6b7280'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`flex-1 h-12 bg-[#0f2027] text-white rounded-[24px] px-6 transition-colors duration-200 ${
              isLoading 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:bg-[#1e3a3a] cursor-pointer'
            }`}
            style={{
              fontFamily: 'Figtree',
              fontWeight: 600,
              fontSize: '16px'
            }}
          >
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}